import { VdTracking } from '@vend/vend.js'

import { IPromise, IScope } from 'angular'
import { EVENTS } from '../../../constants'
import { STATES } from '../../../../../lib/routes'
import { $q, $state } from '../../../ng-compat/imports'
import { registersStore } from '../../../factories/database/registers-store'
import { UsersStore } from '../../../factories/database/users-store'
import { SaleManager } from '../../../managers/sale-manager'
import { RegisterManager } from '../../../managers/register-manager'
import { TrainingModeManager } from '../../../managers/training-mode-manager'
import { UserManager } from '../../../managers/user-manager'
import { WebRegisterStatusManager } from '../../../managers/web-register-status-manager'
import { SyncManager } from '../../../managers/sync-manager'
import { SaleSyncManager } from '../../../managers/sale-sync-manager'
import type { RegisterModals } from '../../../modals/register-modals'

/**
 * Controller class for the register actions popover
 *
 * @class RegisterActionsPopoverController
 */
export class RegisterActionsPopoverController {
  /**
   * Determines if the switch register link can be shown.
   */
  public canShowSwitchRegisterItem = false

  constructor(
    $scope: IScope,
    public close: $TSFixMe,
    private registerManager: RegisterManager,
    public saleSyncManager: SaleSyncManager,
    private saleManager: SaleManager,
    public trainingModeManager: TrainingModeManager,
    private userManager: UserManager,
    private webRegisterStatusManager: WebRegisterStatusManager,
    usersStore: UsersStore,
    private registerModals: RegisterModals,
    syncManager: SyncManager,
    private saleModals: $TSFixMe,
    private trainingModeModals: $TSFixMe,
    private vdTracking: VdTracking
  ) {
    'ngInject'

    $q.all([
      syncManager.whenCompleteEntityDataAvailable('outlets'),
      syncManager.whenCompleteEntityDataAvailable('registers'),
    ]).then(() => {
      registersStore.on('sync', this._setCanShowSwitchRegisterLink, this)
      usersStore.on('sync', this._setCanShowSwitchRegisterLink, this)
      userManager.on(
        'loggedInUserChange',
        this._setCanShowSwitchRegisterLink,
        this
      )

      // Close the popover if a sale sync occurs or the register transitions offline
      this.webRegisterStatusManager.on(EVENTS.statusChange, this.close, this)
      this.saleSyncManager.on('saleSyncRetry', this.close, this)

      this.trainingModeManager.on(
        'trainingModeEnabledChange',
        this._setCanShowSwitchRegisterLink,
        this
      )
      this._trackRegisterActions('Register Actions Shown')

      this._setCanShowSwitchRegisterLink()
    })

    $scope.$on('$destroy', () => {
      registersStore.off('sync', this._setCanShowSwitchRegisterLink)
      usersStore.off('sync', this._setCanShowSwitchRegisterLink)
      userManager.off('loggedInUserChange', this._setCanShowSwitchRegisterLink)
      this.webRegisterStatusManager.off(EVENTS.statusChange, this.close)
      this.saleSyncManager.off('saleSyncRetry', this.close)
      this.trainingModeManager.off(
        'trainingModeEnabledChange',
        this._setCanShowSwitchRegisterLink
      )
    })
  }

  /**
   * Toggle training mode, showing a confirmation modal when entering and exiting. If a sale is in progress prior to
   * enabling training mode, the retailer will be prompted for a course of action.
   *
   * @method toggleTrainingMode
   */
  public toggleTrainingMode() {
    this._closePopoverAndHideSideNav()

    if (this.trainingModeManager.trainingModeEnabled) {
      this._trackRegisterActions('Exit Training Mode')
      return this.trainingModeModals
        .exitTrainingModeConfirmation()
        .then(() => this.trainingModeManager.exitTrainingMode())
        .then(() => $state.go(STATES.SALE, {}, { location: 'replace' }))
    }

    this._trackRegisterActions('Switch to Training Mode')
    this._actionExistingSale(this.saleModals.CONTEXTS.TRAINING_MODE).then(
      () => {
        this.trainingModeManager.initiateTrainingMode()
        this.trainingModeModals
          .confirmTrainingMode()
          .then(() => this.trainingModeManager.initiateTrainingTour())
      }
    )
  }

  /**
   * Initiate the switching of registers by showing the select register modal.
   *
   * @method switchRegister
   */
  public switchRegister() {
    this._closePopoverAndHideSideNav()

    this._canSwitchRegister().then(() => {
      this._trackRegisterActions('Switch Register')
      this.registerModals.selectRegister(true)
    })
  }

  /**
   * Trigger a sync of unsynced sales.
   *
   * @method syncOfflineSales
   */
  public syncOfflineSales() {
    this._closePopoverAndHideSideNav()

    this._trackRegisterActions('Sync Offline Sales')

    this.saleSyncManager.retryUnsyncedSales()
  }

  /**
   * Helper for dealing with active sales that need to be actioned prior to fulfiling a register action.
   *
   * @private
   * @method _actionExistingSale
   *
   * @param {String} context
   *        The sale modal context; see saleModals.CONTEXTS.
   *
   * @return {Promise} A promise resolved if there is not an active sale
   */
  private _actionExistingSale(context: string): IPromise<void> {
    return $q((resolve, reject) => {
      return this.saleManager
        .actionExistingSaleIfRequired({ context })
        .then((response: $TSFixMe) => {
          if (response.hasActiveSale) {
            // Only navigate if we're not already within a Sale state
            if (!$state.includes(STATES.CURRENT_SALE)) {
              $state.go(STATES.SALE)
            }
            return reject()
          } else {
            // If we are on the payment view without an active sale, transition to the Sale state
            if ($state.includes(STATES.PAYMENT_PANEL)) {
              $state.go(STATES.SALE)
            }
            return resolve()
          }
        })
    })
  }

  /**
   * Close the popover instance and the sidenav (if it is open)
   *
   * @private
   * @method _closePopoverAndHideSideNav
   */
  private _closePopoverAndHideSideNav() {
    this.close()
  }

  /**
   * Determine if the switching of registers can occur. Prevented if the register has failed to sync (ERROR or
   * ATTEMPTED) sales or offline (UPLOAD) sales. Also prevented if a current active sale is in progress.
   *
   * @private
   * @method _canSwitchRegister
   *
   * @return {Promise} A promise resolved if the retailer is able to switch registers
   */
  private _canSwitchRegister(): IPromise<void> {
    return $q((resolve, reject) => {
      let canSwitchRegisterResolver = $q.resolve()

      if (this.saleSyncManager.uploadSalesCount) {
        // Check for offline sales first
        canSwitchRegisterResolver = this.registerModals.actionOfflineSales()
      } else if (
        this.saleSyncManager.erroredSalesCount +
          this.saleSyncManager.attemptedSalesCount >
        0
      ) {
        // Reject immediately if there are any failed to sync sales
        this.registerModals.actionErroredSales()
        return reject()
      } else {
        canSwitchRegisterResolver = this._actionExistingSale(
          this.saleModals.CONTEXTS.SWITCH_REGISTERS
        )
      }

      return canSwitchRegisterResolver.then(resolve, reject)
    })
  }

  /**
   * Determine if we can display the switch register link.
   *
   * @private
   * @method _setCanShowSwitchRegisterLink
   */
  private _setCanShowSwitchRegisterLink() {
    if (
      !this.webRegisterStatusManager.isOnline() ||
      this.trainingModeManager.trainingModeEnabled
    ) {
      this.canShowSwitchRegisterItem = false
      return
    }
    const activeUser = this.userManager.getLoggedInUser()
    this.registerManager
      .getAllRegistersByOutlet({
        restrictedOutlets: activeUser.restricted_outlet_ids!,
      })
      .then((registersByOutlet: $TSFixMe) => {
        if (!registersByOutlet.length) {
          this.canShowSwitchRegisterItem = false
          return
        }
        // User is able to switch register if they either have access to multipile outlets, or a single outlet with
        // more than one register.
        const hasMultipleOutlets = registersByOutlet.length > 1
        const hasSingleOutletWithMultipleRegisters =
          registersByOutlet[0].registers &&
          registersByOutlet[0].registers.length > 1

        this.canShowSwitchRegisterItem =
          hasMultipleOutlets || hasSingleOutletWithMultipleRegisters
      })
  }

  /**
   * Track register actions
   *
   * @private
   * @method _trackRegisterActions
   *
   * @param {String} event
   *        Event identifier
   */
  private _trackRegisterActions(event: string) {
    this.vdTracking.trackUserEvent({
      eventName: 'Sidenav Register Actions',
      metadata: {
        Event: event,
      },
    })
  }
}
